import { GlassPanel } from "@/components/ui/glass-panel";
import { Sun, SunMedium, AlertTriangle } from "lucide-react";

interface UvForecastProps {
  uv: any;
}

const UV_LEVELS = [
  { max: 2, label: "Low", color: "text-green-400", bg: "bg-green-500/10" },
  { max: 5, label: "Moderate", color: "text-yellow-400", bg: "bg-yellow-500/10" },
  { max: 7, label: "High", color: "text-orange-400", bg: "bg-orange-500/10" },
  { max: 10, label: "Very High", color: "text-red-400", bg: "bg-red-500/10" },
  { max: Infinity, label: "Extreme", color: "text-purple-400", bg: "bg-purple-500/10" },
];

function getUvLevel(uvi: number) {
  return UV_LEVELS.find((l) => uvi <= l.max) || UV_LEVELS[UV_LEVELS.length - 1];
}

export function UvForecast({ uv }: UvForecastProps) {
  if (!uv || !Array.isArray(uv) || uv.length === 0) return null;

  const peakUv = uv.reduce((max: number, h: any) => Math.max(max, h.uvi ?? 0), 0);
  const peakLevel = getUvLevel(peakUv);
  const peakHour = uv.find((h: any) => (h.uvi ?? 0) === peakUv);
  const peakTime = peakHour
    ? new Date(peakHour.dt * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : "";

  const safeTimes = uv.filter((h: any) => (h.uvi ?? 0) <= 2).length;

  return (
    <GlassPanel intensity="low" className="p-4">
      <div className="flex items-start gap-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg shrink-0 ${peakLevel.bg}`}>
          <Sun className={`h-4.5 w-4.5 ${peakLevel.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold mb-1">UV Forecast</h3>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold">{peakUv.toFixed(1)}</span>
            <span className={`text-xs font-semibold ${peakLevel.color}`}>{peakLevel.label}</span>
          </div>
          {peakTime && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Peak at {peakTime} • {safeTimes}/24 safe hours
            </p>
          )}
          <div className="mt-3 flex items-end gap-px h-8">
            {uv.slice(0, 24).map((h: any, i: number) => {
              const level = getUvLevel(h.uvi ?? 0);
              const barHeight = Math.max(3, ((h.uvi ?? 0) / Math.max(peakUv, 1)) * 100);
              return (
                <div
                  key={i}
                  className="flex-1 min-w-[2px] rounded-t-sm"
                  style={{
                    height: `${barHeight}%`,
                    backgroundColor:
                      (h.uvi ?? 0) > 10 ? "rgb(168 85 247)" :
                      (h.uvi ?? 0) > 7 ? "rgb(239 68 68)" :
                      (h.uvi ?? 0) > 5 ? "rgb(249 115 22)" :
                      (h.uvi ?? 0) > 2 ? "rgb(234 179 8)" :
                      "rgb(74 222 128)",
                  }}
                />
              );
            })}
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
            <span>6am</span>
            <span>Noon</span>
            <span>6pm</span>
          </div>
        </div>
      </div>
    </GlassPanel>
  );
}